import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { matchPath } from 'react-router-dom';

import ModalPortal from '../../../components/modal-portal';
import { ReturnsUnsavedChangesModal } from '../../../components/modals/returns-unsaved-changes-modal';
import { useNavigationBlocker } from '../../../hooks/routing/use-navigation-blocker';
import { closedOrderDetailModal, goToOrderDetails } from '../../../store/orders/order-details/orders.details.actions';
import { usePendingReturn } from '../hooks/use-pending-return';
import { OrderDetailsSalesOrderRoute } from '../routing';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';
import { noop } from 'ui-components/default-props/noop';

/**
 * @type {React.Context<{
 *  cancelPendingReturn: () => void,
 *  cancelPendingEnhancedReturn: (promptUser?: boolean) => void,
 *  cancelPendingReturnToCloseOrderDetails: () => void,
 *  resetHasPendingReturn: () => void,
 *  hasPendingReturn: boolean,
 *  isPendingReturnSelected: boolean,
 * }>}
 */
const PendingReturnContext = createContext({
  cancelPendingReturn: noop,
  cancelPendingEnhancedReturn: noop,
  cancelPendingReturnToCloseOrderDetails: noop,
  resetHasPendingReturn: noop,
  hasPendingReturn: false,
  isPendingReturnSelected: false,
});

export const usePendingReturnContext = () => useContext(PendingReturnContext);

const CancelPendingReturnReason = {
  Cancel: 'Cancel',
  CloseOrderDetails: 'CloseOrderDetails',
  NavigateToNewSalesOrder: 'NavigateToNewSalesOrder',
};

const PendingReturnContextProvider = ({ children, salesOrderId, returnId }) => {
  const [showCancelConfirmationDialog, setShowCancelConfirmationDialog] = useState(false);
  const [cancelPendingReturnReason, setCancelPendingReturnReason] = useState(undefined);

  const dispatch = useDispatch();

  const { hasPendingReturn, isPendingReturnSelected, resetHasPendingReturn } = usePendingReturn(returnId);

  const trackCreateReturn = useTrackWorkflow(Workflows.CREATE_RETURN_FLOW);

  const showCancelPendingReturnModal = useCallback((reason) => {
    setCancelPendingReturnReason(reason);
    setShowCancelConfirmationDialog(true);
  }, []);

  const cancelPendingReturn = useCallback(() => {
    showCancelPendingReturnModal(CancelPendingReturnReason.Cancel);
  }, [showCancelPendingReturnModal]);

  const cancelPendingReturnToCloseOrderDetails = useCallback(() => {
    showCancelPendingReturnModal(CancelPendingReturnReason.CloseOrderDetails);
  }, [showCancelPendingReturnModal]);

  const cancelPendingReturnToNavigateToNewSalesOrder = useCallback(() => {
    showCancelPendingReturnModal(CancelPendingReturnReason.NavigateToNewSalesOrder);
  }, [showCancelPendingReturnModal]);

  // If the user has a pending return and is attempting to navigate away from the
  // current sales order (next/prev order, close modal, etc.) block the navigation
  const shouldBlockNavigation = useCallback(
    ({ nextLocation }) => {
      if (!hasPendingReturn) {
        return false;
      }

      const match = matchPath(nextLocation?.pathname, {
        path: OrderDetailsSalesOrderRoute,
      });

      const newSalesOrderId = match?.params?.salesOrderId;

      if (!newSalesOrderId) {
        cancelPendingReturnToCloseOrderDetails();
        return true;
      }

      if (newSalesOrderId !== salesOrderId) {
        cancelPendingReturnToNavigateToNewSalesOrder();
        return true;
      }

      return false;
    },
    [cancelPendingReturnToCloseOrderDetails, cancelPendingReturnToNavigateToNewSalesOrder, hasPendingReturn, salesOrderId],
  );

  const { unblockNavigation, continueLastNavigation } = useNavigationBlocker(shouldBlockNavigation);

  const closeConfirmDialog = useCallback(() => {
    setShowCancelConfirmationDialog(false);
    setCancelPendingReturnReason(undefined);
  }, []);

  const unsavedReturnDialogModalFrameworkMethods = useMemo(
    () => ({
      closeModal: closeConfirmDialog,
    }),
    [closeConfirmDialog],
  );

  const onCancelPendingReturnConfirmed = useCallback(() => {
    unblockNavigation();
    closeConfirmDialog();
    resetHasPendingReturn();
    trackCreateReturn(WorkflowSteps.CANCEL);

    switch (cancelPendingReturnReason) {
      case CancelPendingReturnReason.CloseOrderDetails:
        dispatch(closedOrderDetailModal());
        break;
      case CancelPendingReturnReason.NavigateToNewSalesOrder:
        continueLastNavigation();
        break;
      case CancelPendingReturnReason.Cancel:
      default:
        dispatch(goToOrderDetails({ salesOrderId }));
        break;
    }
  }, [
    cancelPendingReturnReason,
    closeConfirmDialog,
    continueLastNavigation,
    dispatch,
    resetHasPendingReturn,
    salesOrderId,
    trackCreateReturn,
    unblockNavigation,
  ]);

  const cancelPendingEnhancedReturn = useCallback(
    (promptUser = false) => {
      if (promptUser) {
        return cancelPendingReturn();
      }
      onCancelPendingReturnConfirmed();
    },
    [cancelPendingReturn, onCancelPendingReturnConfirmed],
  );

  const value = useMemo(() => {
    return {
      cancelPendingReturn,
      cancelPendingEnhancedReturn,
      cancelPendingReturnToCloseOrderDetails,
      hasPendingReturn,
      isPendingReturnSelected,
      resetHasPendingReturn,
    };
  }, [
    cancelPendingReturn,
    cancelPendingEnhancedReturn,
    cancelPendingReturnToCloseOrderDetails,
    hasPendingReturn,
    isPendingReturnSelected,
    resetHasPendingReturn,
  ]);

  return (
    <PendingReturnContext.Provider value={value}>
      {children}
      {showCancelConfirmationDialog && (
        <ModalPortal onClose={closeConfirmDialog}>
          <ReturnsUnsavedChangesModal
            modalFrameworkMethods={unsavedReturnDialogModalFrameworkMethods}
            onContinue={onCancelPendingReturnConfirmed}
          />
        </ModalPortal>
      )}
    </PendingReturnContext.Provider>
  );
};

export default PendingReturnContextProvider;

PendingReturnContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  returnId: PropTypes.string,
  salesOrderId: PropTypes.string.isRequired,
};
